import React, { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './CexInputPhone.styles.scss';

// Definición de nuestro propio tipo para códigos de país
type CountryCode =
    | 'US' // Estados Unidos
    | 'ES' // España
    | 'IN' // India
    | 'MX' // México
    | 'CA' // Canadá
    | 'GB' // Reino Unido
    | 'DE' // Alemania
    | 'FR' // Francia
    | 'IT' // Italia
    | 'BR' // Brasil
    | 'AR' // Argentina
    | 'CL' // Chile
    | 'CO' // Colombia
    | 'PE' // Perú
    | 'AU' // Australia
    | 'JP' // Japón
    | 'CN' // China
    | 'RU' // Rusia
    | undefined; // Para permitir el valor undefined

interface CexInputPhoneProps {
    value: string;
    onChange: (value: string) => void;
    error?: string;
    placeholder?: string;
    initialCountry?: CountryCode; // Ahora usamos nuestro tipo personalizado
    readOnly?: boolean; // Nueva prop para modo lectura
    label?: string;
    height?: string; // Nueva prop para controlar la altura
    width?: string; // Nueva prop para controlar el ancho
    outsideEmr?: boolean;
}

const CexInputPhone = ({
    value,
    onChange,
    error,
    placeholder = 'Enter phone number',
    initialCountry = 'US', // Valor predeterminado es EE.UU.
    readOnly = false, // Por defecto, el campo es editable
    label,
    height,
    width,
}: CexInputPhoneProps) => {
    // Estado para determinar si el input tiene un valor preexistente
    const [hasInitialValue, setHasInitialValue] = useState(false);
    // Estado para almacenar el último valor válido
    const [lastValidValue, setLastValidValue] = useState<string>(value || '');

    useEffect(() => {
        // Si el input tiene un valor inicial, marcarlo como preexistente
        if (value && value.length > 1) {
            setHasInitialValue(true);
            setLastValidValue(value);
        }
    }, []);

    // Manejador de cambios seguro que verifica si el componente está en modo readOnly
    const handleChange = (val: string | undefined) => {
        // Si está en modo readOnly, no hacemos nada
        if (readOnly) return;

        // Si el valor es undefined o vacío, no permitimos borrar todo y mantenemos el último valor válido
        // que contenga al menos el código de país
        if (!val || val.trim() === '' || !val.includes('+')) {
            onChange(lastValidValue);
            return;
        }

        // Si tenemos un valor válido, actualizamos el estado y llamamos al callback
        setLastValidValue(val);
        onChange(val);
    };

    // Check if URL contains labresults to determine if it's outside EMR
    const urlPath = window.location.hash;
    // También es outside si incluye "telemed"
    const isOutsideEmr = !urlPath.toLowerCase().includes('emr') && !urlPath.toLowerCase().includes('telemedicine');

    return (
        <div
            className='cex-phone-input-container'
            style={{
                ...(label ? { display: 'flex', gap: '10px' } : {}),
                ...(height ? { height } : {}),
                ...(width ? { width } : {}),
            }}
        >
            {label && <label className='cex-phone-input-label'>{label}</label>}
            <PhoneInput
                placeholder={placeholder}
                value={value || ''} // Aseguramos que value nunca sea undefined o null
                onChange={handleChange}
                defaultCountry={hasInitialValue ? undefined : initialCountry}
                className={`phone-input-custom ${error ? 'phone-input-error' : ''} `}
                maxLength={16}
                international={true}
                limitMaxLength={true}
                smartCaret={true}
                withCountryCallingCode={true}
                countryCallingCodeEditable={false} // Esto debería prevenir que se edite el código de país
                countriesLimit={1000}
                numberInputProps={{
                    inputMode: 'tel',
                    className: `phone-number-input-field ${error ? 'phone-input-error' : ''} ${
                        isOutsideEmr ? 'outside-emr-styles' : 'inside-emr-styles'
                    }`,
                    readOnly: readOnly, // Aplicamos la prop readOnly al input
                    style: height ? { height } : undefined,
                }}
                disabled={readOnly} // También deshabilitamos el selector de país cuando es readOnly
                displayInitialValueAsLocalNumber={false}
                countrySelectProps={{
                    unicodeFlags: true,
                }}
                style={
                    height
                        ? ({
                              height,
                          } as React.CSSProperties)
                        : undefined
                }
            />
            {error && (
                <span className='phone-input-error-message' style={{ fontSize: '10px' }}>
                    {error}
                </span>
            )}
        </div>
    );
};

export default CexInputPhone;
